import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { useFamilyStore } from '@/stores/family-store';
import { apiGet, apiPost } from '@/services/api-client';
import { useTranslation } from 'react-i18next';
import { Check, X, Heart } from 'lucide-react';

interface Invite {
  id: string;
  familyId: string;
  familyName?: string;
  invitedBy?: string;
  invitedByEmail?: string;
  inviteeEmail?: string;
  invitedEmail?: string;
  email?: string;
  message?: string;
  status?: string;
}

export default function InvitePrompt() {
  const { t } = useTranslation();
  const { user, setUser } = useAuthStore((s) => ({ user: s.user, setUser: s.setUser }));
  const { fetchFamily, declineInvitation } = useFamilyStore((s) => ({ fetchFamily: s.fetchFamily, declineInvitation: s.declineInvitation }));
  const [invite, setInvite] = useState<Invite | null>(null);
  const [loading, setLoading] = useState(false);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const lastActive = useRef<HTMLElement | null>(null);

  useEffect(() => {
    let mounted = true;
    async function checkInvites() {
      if (!user || user.familyId) return;
      try {
        const res = await apiGet<Invite[]>('/families/invites/mine');
        if (res.success && res.data && res.data.length > 0) {
          if (!mounted) return;
          setInvite(res.data[0]);
        }
      } catch (err) {
        console.error('Failed checking invites', err);
      }
    }
    checkInvites();
    return () => { mounted = false; };
  }, [user]);


  const handleAccept = async () => {
    if (!user || !invite) return;
    setLoading(true);
    try {
      const res = await apiPost(`/families/${invite.familyId}/accept`, { inviteId: invite.id });
      if (res.success) {
        // Mark user as onboarded so onboarding UI is skipped
        try {
          setUser({ ...user, onboardingCompleted: true });
        } catch { /* ignore */ }

        await fetchFamily(invite.familyId);
        
        // Refresh page and navigate to overview
        window.location.href = '/app';
      }
      setInvite(null);
    } catch (err) {
      console.error('Accept invite failed', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = useCallback(async () => {
    setLoading(true);
    try {
      if (!invite) return;
      // call store action which calls backend decline and removes locally
      await declineInvitation(invite.id);
      setInvite(null);
    } catch (err) {
      console.error('Decline invite failed', err);
    } finally {
      setLoading(false);
    }
  }, [declineInvitation, invite]);

  // Lock background scroll and trap focus while modal is open
  useEffect(() => {
    if (!invite) return;
    lastActive.current = document.activeElement as HTMLElement | null;
    const el = modalRef.current;

    import('@/utils/scrollLock').then(mod => mod.lockScroll()).catch(() => { try { document.body.style.overflow = 'hidden'; } catch (_e2) { void _e2; } });

    const focusable = el ? el.querySelectorAll<HTMLElement>('button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])') : null;
    if (focusable && focusable.length > 0) focusable[0].focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (!el) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        handleDecline();
        return;
      }
      if (e.key === 'Tab') {
        const nodes = Array.from(el.querySelectorAll<HTMLElement>('button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])')).filter(n => !n.hasAttribute('disabled'));
        if (nodes.length === 0) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    window.addEventListener('keydown', onKeyDown, { capture: true });

    // prevent wheel and touchmove to block scrolling on desktop and mobile
    const preventScroll = (ev: Event) => {
      try {
        if (el && ev.target && (ev.target as Node) && el.contains(ev.target as Node)) return;
      } catch (err) {
        console.error('preventScroll check failed', err);
      }
    };

    document.addEventListener('wheel', preventScroll, { passive: false, capture: true });
    document.addEventListener('touchmove', preventScroll, { passive: false, capture: true });

    return () => {
      window.removeEventListener('keydown', onKeyDown, { capture: true } as EventListenerOptions);
      document.removeEventListener('wheel', preventScroll as EventListener, { capture: true } as EventListenerOptions);
      document.removeEventListener('touchmove', preventScroll as EventListener, { capture: true } as EventListenerOptions);

      import('@/utils/scrollLock').then(mod => mod.unlockScroll()).catch(() => { try { document.body.style.overflow = ''; document.documentElement.style.overflow = ''; } catch (_e2) { void _e2; } });
      try { lastActive.current?.focus(); } catch (err) { console.error('focus failed', err); }
    };
  }, [invite, handleDecline]);

  if (!invite) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-0">
      <div ref={modalRef} className="w-full h-screen bg-background p-4 overflow-auto pb-20 sm:pb-0 sm:rounded-xl sm:max-w-md sm:max-h-[80vh] sm:overflow-auto sm:p-6">
          <div className="mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2"><Heart className="h-5 w-5 text-white" />{t('family.title')}</h3>
          <div className="h-2" />
          <p className="text-sm text-muted-foreground">{t('family.invitedToFamilyLong', { defaultValue: 'Du har blivit inbjuden till en familj i Beredskapshubben.' })}</p>
        </div>

        <div className="mt-4 space-y-3">
          <div>
            <p className="text-sm text-muted-foreground">{t('family.inviteLabelFamily', { defaultValue: 'Familj' })}</p>
            <p className="font-medium soft-wrap">{invite.familyName}</p>
          </div>

          <div>
            <p className="text-sm text-muted-foreground">{t('family.inviteLabelSentTo', { defaultValue: 'Skickat till' })}</p>
            <p className="font-medium soft-wrap">{invite.invitedEmail ?? invite.inviteeEmail ?? invite.email ?? ''}</p>
          </div>

          <div>
            <p className="text-sm text-muted-foreground">{t('family.inviteLabelSentFrom', { defaultValue: 'Skickat från' })}</p>
            <p className="font-medium soft-wrap">{invite.invitedBy}</p>
          </div>

          {invite.message && (
            <div>
              <p className="text-sm text-muted-foreground">{t('family.inviteLabelMessage', { defaultValue: 'Meddelande' })}</p>
              <p className="mt-1 italic text-sm break-words">"{invite.message}"</p>
            </div>
          )}

          <div>
            <p className="text-sm text-muted-foreground">{t('family.inviteHowItWorks', { defaultValue: 'Om du accepterar denna inbjudan så kommer du att börja använda familjens beredskapsprofil i appen istället för din egen. Om du väljer att lämna familjeläget så kommer din gamla profil att finnas kvar här.' })}</p>
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button onClick={handleDecline} className="flex-1 rounded-lg border px-4 py-2 hover:bg-muted" disabled={loading}>
            <X className="h-4 w-4 inline mr-2" /> {t('family.declineInvitation')}
          </button>
          <button onClick={handleAccept} className="flex-1 flex items-center justify-center gap-2 btn-primary-custom px-4 py-2" disabled={loading}>
            <Check className="h-4 w-4" /> {t('family.acceptInvitation')}
          </button>
        </div>
      </div>
    </div>
  );
}
